import { HTMLProps, useState } from 'react';
import { Arrow2 } from '../../Icons';
import { useCart } from '@shared/cart-react';
import { Input } from '@shared/cove-ui';
import { LayoutVariant } from '../Layout';
import { useTranslation } from 'react-i18next';

const SubscribeForm: React.FC<
  {
    handleSubscribe: (email: string) => Promise<{ status: string }>;
    variant?: LayoutVariant;
  } & HTMLProps<HTMLDivElement>
> = ({ handleSubscribe, ...props }) => {
  const { t } = useTranslation('common', { keyPrefix: 'layout.footer' });
  const cart = useCart();
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [showEmailStatus, setShowEmailStatus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  let timeout: NodeJS.Timeout;

  const emailIsValid = async () => {
    if (!emailRegex.test(email)) {
      setIsEmailValid(false);
      setEmailStatus('Please enter a valid address.');
      setShowEmailStatus(true);

      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setShowEmailStatus(false);
        setEmailStatus('');
        setIsEmailValid(true);
      }, 10000);
      return;
    }

    setIsEmailValid(true);
    setShowEmailStatus(false);
    setIsSubmitting(true);

    const res = await handleSubscribe(email);
    if (res?.status !== 'success') {
      setEmailStatus(res?.status);
      setShowEmailStatus(true);
    } else {
      setEmailStatus('Welcome! Thanks for signing up.');
      setShowEmailStatus(true);
      cart.updateCustomer({ email });
      setTimeout(() => setEmail(''), 10000);
    }
    setIsSubmitting(false);
    setTimeout(() => {
      setEmailStatus('');
      setShowEmailStatus(false);
    }, 10000);
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      emailIsValid();
    }
  };

  return (
    <>
      <div
        className="mt-cove-25 max-w-button group w-full md:mt-[105px]"
        data-email-invalid={!isEmailValid}
        {...props}
      >
        <span className="mt-cove-25 md:mb-cove-25 text-lg font-semibold md:mt-0">
          Get Cove Deals
        </span>
        <div className="relative flex w-full max-w-[268px] items-center">
          <div className="w-full min-w-0 shrink-0 overflow-hidden truncate text-sm">
            <Input
              name="email"
              placeholder="Enter your email address"
              onChange={e => setEmail(e.target.value)}
              onKeyDown={onEnter}
              value={email}
              invalid={!isEmailValid}
              className="pr-cove-35 placeholder:text-cove-dove w-full border-x-0 border-b border-t-0 border-b-black bg-transparent pl-0 [&_~_label]:pl-0 [&_~_label]:text-sm"
            />
          </div>
          <button
            onClick={() => emailIsValid()}
            disabled={isSubmitting}
            className='w-cove-35 h-cove-35 bg-cove-dove border-cove-navy-blue max-w-cove-35 max-h-cove-35 group-data-[email-invalid="true"]:text-cove-red group-data-[email-invalid="true"]:border-cove-red flex shrink-0 items-center justify-center rounded-full border p-0 shadow'
          >
            {isSubmitting ? (
              <div className="border-cove-navy-blue h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
            ) : (
              <Arrow2 className="text-cove-navy-blue" />
            )}
            <span className="sr-only">
              {isSubmitting ? 'Submitting' : 'Submit'}
            </span>
          </button>
        </div>
        {showEmailStatus && (
          <p className='mt-cove-50 group-data-[email-invalid="true"]:text-cove-red m-0 text-sm'>
            {emailStatus}
          </p>
        )}
      </div>
    </>
  );
};

export default SubscribeForm;
